import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { invariant } from "@searchui-searchforms/shared/utils";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";
import { createSafeContext } from "@/shared/utils/createSafeContext";
import type { ExtendedValidationFieldConfig } from "./types";
import { useGenerateConfigWithPattern } from "./useGenerateConfigWithPattern";

type CleanupFn = () => void;

interface ValidatorContextValue {
    attachFieldValidation: (fieldId: string, config: ExtendedValidationFieldConfig) => CleanupFn;
}

const [ValidatorProviderBase, useValidatorContext] = createSafeContext<ValidatorContextValue>("FormValidatorContext");

interface ValidatorProviderProps {
    children: ReactNode;
    formId: string;
}

export const ValidatorProvider = ({ children, formId }: ValidatorProviderProps) => {
    const validatorRef = useRef<UiValidatorInstance | null>(null);
    // Keep track of calls which may happen before validatorRef.current is attached
    const queue = useRef<Map<string, ExtendedValidationFieldConfig>>(new Map());
    const generateConfigWithPattern = useGenerateConfigWithPattern();

    const validatorLibLoaded = useUIComponentLoaded("validator");

    const attachFieldValidation = useCallback(
        (fieldId: string, extendedConfig: ExtendedValidationFieldConfig) => {
            if (validatorRef.current) {
                const config = generateConfigWithPattern(extendedConfig, validatorRef.current);
                const modifyingField = !!validatorRef.current!.fields[fieldId];
                validatorRef.current!.addField(fieldId, config, modifyingField);
            } else {
                queue.current.set(fieldId, extendedConfig);
            }
            return () => {
                // If we try to remove non-registered field, validator would throw an error
                if (validatorRef.current?.fields[fieldId]) {
                    validatorRef.current.removeFields(fieldId);
                }
            };
        },
        [generateConfigWithPattern],
    );

    useEffect(() => {
        if (validatorLibLoaded && !validatorRef.current) {
            const formNode = document.getElementById(formId);
            invariant(formNode, "Did not find form element");
            // Pass detached dummy elements for submitButton and resetButton to prevent
            // the validator from directly manipulating real DOM buttons' disabled state.
            // The validator uses querySelector('[type="submit"]') by default, which only
            // finds the first button — causing inconsistency when multiple submit buttons exist.
            // React manages button states; the validator should only handle field validation.
            const dummySubmit = document.createElement("button");
            const dummyReset = document.createElement("button");

            validatorRef.current = window.ui.validator(formNode, {
                fields: {},
                allowMultipleSubmissions: true,
                submitButton: dummySubmit,
                resetButton: dummyReset,
                onSubmit: ({ valid }: { valid: boolean }) => valid,
            });
            if (queue.current.size) {
                queue.current.forEach((config, fieldId) => {
                    attachFieldValidation(fieldId, config);
                });
                queue.current.clear();
            }
            return () => {
                validatorRef.current?.destroy();
                validatorRef.current = null;
            };
        }
    }, [attachFieldValidation, formId, validatorLibLoaded]);

    const providerValue = useMemo(() => ({ attachFieldValidation }), [attachFieldValidation]);

    return <ValidatorProviderBase value={providerValue}>{children}</ValidatorProviderBase>;
};

export { useValidatorContext };
